import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Plus } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api, ApiError } from "../../shared/api/client";
import type {
  PageResponse,
  ReleaseScopeChangeReason,
  ReleaseSummary,
  ReleaseType,
} from "../../shared/types/api";
import { Button, Field, InlineAlert, Input, Select, Textarea } from "../../shared/ui";
import { RELEASE_TYPE_LABELS, SCOPE_CHANGE_REASONS } from "./releaseVocabulary";

const TYPES: ReleaseType[] = [
  "MAJOR", "MINOR", "PATCH", "HOTFIX", "EMERGENCY",
  "UPGRADE", "INFRASTRUCTURE", "CONFIGURATION",
];

/** What a caller needs to send: the release, and the reason if one is required. */
export interface ReleaseChoice {
  releaseId: string | null;
  scopeChangeReason: ReleaseScopeChangeReason | null;
  scopeChangeComment: string | null;
}

/**
 * Choosing which release carries a piece of work (§10, §14).
 *
 * <p>Frozen releases are offered rather than hidden, and asking for a reason instead. Teams
 * genuinely do need to put an emergency fix into a release whose scope was settled last week —
 * hiding the option does not stop that happening, it just means it happens somewhere the record
 * cannot see, which is the outcome §13 argues is worse than the change itself.
 *
 * <p>A release can be created from here, but through the same three fields the release form
 * asks for. Type decides which approvals and tests apply, so a release created as a side effect
 * of raising a ticket still has to have that chosen rather than defaulted quietly.
 */
export function ReleasePicker({
  projectId,
  value,
  currentReleaseId,
  onChange,
  label = "Release",
  hint = "Optional. Work is often created before anybody knows which release will carry it.",
}: {
  projectId: string;
  value: ReleaseChoice;
  /** What the item is in already, so leaving it alone is not treated as a change. */
  currentReleaseId?: string | null;
  onChange: (choice: ReleaseChoice) => void;
  label?: string;
  hint?: string;
}) {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [newVersion, setNewVersion] = useState("");
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState<ReleaseType>("MINOR");
  const [createError, setCreateError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const releases = useQuery({
    queryKey: ["releases", "picker", projectId],
    queryFn: () =>
      api.get<PageResponse<ReleaseSummary>>(
        `/api/v1/projects/${projectId}/releases?size=200`,
      ),
    enabled: projectId !== "",
  });

  const options = (releases.data?.content ?? []).filter(
    (release) => !["COMPLETED", "CANCELLED", "ARCHIVED"].includes(release.status),
  );
  const chosen = options.find((release) => release.id === value.releaseId) ?? null;

  /*
   * The reason is only demanded when this is actually a change to a frozen release. Re-saving an
   * item already in a frozen release must not ask again — nothing moved, and asking would train
   * people to type "n/a".
   */
  const changing = value.releaseId !== (currentReleaseId ?? null);
  const needsReason = changing && chosen?.scopeFrozen === true;
  const leaving = changing && currentReleaseId != null;
  const reasonRequired = needsReason || leaving;

  const create = async () => {
    setBusy(true);
    setCreateError(null);
    try {
      const created = await api.post<{ id: string }>(
        `/api/v1/projects/${projectId}/releases`,
        { releaseName: newName.trim(), version: newVersion.trim(), releaseType: newType },
      );
      await queryClient.invalidateQueries({ queryKey: ["releases", "picker", projectId] });
      onChange({ ...value, releaseId: created.id });
      setCreating(false);
      setNewVersion("");
      setNewName("");
    } catch (caught) {
      setCreateError(
        caught instanceof ApiError ? caught.message : "Could not create that release.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-3">
      <Field label={label} hint={hint}>
        <div className="flex gap-2">
          <Select
            value={value.releaseId ?? ""}
            onChange={(event) =>
              onChange({
                releaseId: event.target.value === "" ? null : event.target.value,
                scopeChangeReason: null,
                scopeChangeComment: null,
              })
            }
          >
            <option value="">No release</option>
            {options.map((release) => (
              <option key={release.id} value={release.id}>
                {release.version} — {release.releaseName}
                {release.scopeFrozen ? " (scope frozen)" : ""}
              </option>
            ))}
          </Select>
          {can("RELEASE_CREATE") && !creating && (
            <Button
              variant="secondary"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => setCreating(true)}
            >
              New
            </Button>
          )}
        </div>
      </Field>

      {creating && (
        <div className="space-y-3 rounded-lg border border-ink-200 bg-ink-50 p-3">
          {createError && <InlineAlert tone="error">{createError}</InlineAlert>}
          <div className="grid grid-cols-2 gap-3">
            <Field label="Version" required>
              <Input
                autoFocus
                value={newVersion}
                placeholder="2.1"
                onChange={(event) => setNewVersion(event.target.value)}
              />
            </Field>
            <Field label="Type" required>
              <Select
                value={newType}
                onChange={(event) => setNewType(event.target.value as ReleaseType)}
              >
                {TYPES.map((type) => (
                  <option key={type} value={type}>
                    {RELEASE_TYPE_LABELS[type]}
                  </option>
                ))}
              </Select>
            </Field>
          </div>
          <Field label="Name" required>
            <Input
              value={newName}
              placeholder="Payments 2.1"
              onChange={(event) => setNewName(event.target.value)}
            />
          </Field>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>
              Cancel
            </Button>
            <Button
              size="sm"
              loading={busy}
              disabled={newVersion.trim() === "" || newName.trim() === ""}
              onClick={() => void create()}
            >
              Create and use
            </Button>
          </div>
        </div>
      )}

      {needsReason && (
        <InlineAlert tone="warning">
          {chosen?.version}&apos;s scope is frozen. Adding to it is allowed and is recorded on the
          release with the reason you give — that record is the point.
        </InlineAlert>
      )}

      {reasonRequired && (
        <>
          <Field
            label={leaving && !value.releaseId ? "Why is this coming out?" : "Why the change?"}
            required
          >
            <Select
              value={value.scopeChangeReason ?? ""}
              onChange={(event) =>
                onChange({
                  ...value,
                  scopeChangeReason:
                    event.target.value === ""
                      ? null
                      : (event.target.value as ReleaseScopeChangeReason),
                })
              }
            >
              <option value="">Choose a reason…</option>
              {SCOPE_CHANGE_REASONS.map(([code, text]) => (
                <option key={code} value={code}>
                  {text}
                </option>
              ))}
            </Select>
          </Field>

          {value.scopeChangeReason === "OTHER" && (
            <Field label="What happened?" required>
              <Textarea
                rows={2}
                value={value.scopeChangeComment ?? ""}
                onChange={(event) =>
                  onChange({ ...value, scopeChangeComment: event.target.value })
                }
              />
            </Field>
          )}
        </>
      )}

      {chosen?.scopeFrozen && !changing && (
        <p className="flex items-center gap-1.5 text-xs text-ink-500">
          <Lock className="h-3 w-3" aria-hidden />
          {chosen.version}&apos;s scope is frozen.
        </p>
      )}
    </div>
  );
}

/** Whether a choice is complete enough to send — the reason rules, in one place. */
export function releaseChoiceReady(
  choice: ReleaseChoice,
  currentReleaseId: string | null,
  releases: ReleaseSummary[],
): boolean {
  const changing = choice.releaseId !== currentReleaseId;
  if (!changing) {
    return true;
  }
  const chosen = releases.find((release) => release.id === choice.releaseId) ?? null;
  const needsReason = chosen?.scopeFrozen === true || currentReleaseId != null;
  if (!needsReason) {
    return true;
  }
  if (choice.scopeChangeReason == null) {
    return false;
  }
  return (
    choice.scopeChangeReason !== "OTHER" || (choice.scopeChangeComment ?? "").trim() !== ""
  );
}
